import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Award, Users, Building, Calendar, Star, CheckCircle, Activity, ArrowLeft, Phone, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { isAuthenticated, getUserRole } from '../utils/auth';
import ConsultationRequestDialog from '../components/ConsultationRequestDialog';

export default function HospitalDetail() {
  const { hospitalId } = useParams();
  const navigate = useNavigate();
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchHospitalDetails();
  }, [hospitalId]);

  const fetchHospitalDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hospitals/${hospitalId}`);
      
      if (!response.ok) {
        throw new Error('Hospital not found');
      }
      
      const data = await response.json();
      setHospital(data.hospital);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestConsultation = () => {
    setConsultOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="loading-state">
          <div className="text-6xl mb-4">🏥</div>
          <p className="text-lg text-gray-600">Loading hospital details...</p>
        </div>
      </div>
    );
  }

  if (error || !hospital) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="error-state">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Hospital Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The hospital you\'re looking for doesn\'t exist.'}</p>
          <Button onClick={() => navigate('/hospitals')} data-testid="back-to-hospitals-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Hospitals
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="hospital-detail-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-orange-100 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🩹</span>
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                MedRahi
              </h1>
              <p className="text-xs text-gray-600">Healthcare Without Borders</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/hospitals')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Hospitals
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50" data-testid="hero-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="font-heading text-4xl font-bold text-gray-900 mb-3" data-testid="hospital-name">
                    {hospital.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-5 h-5" />
                      <span className="text-lg" data-testid="hospital-location">{hospital.city}, {hospital.state}</span>
                    </div>
                    <Badge className="bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="accreditation-badge">
                      <Award className="w-4 h-4 mr-1" />
                      {hospital.accreditation}
                    </Badge>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-testid="hospital-description">
                    {hospital.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="lg:col-span-1">
              <Card className="border-2 border-orange-200 shadow-lg" data-testid="quick-stats-card">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
                  
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Bed Capacity</span>
                    <span className="font-semibold text-gray-900" data-testid="bed-capacity">{hospital.beds}</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Medical Staff</span>
                    <span className="font-semibold text-gray-900" data-testid="medical-staff">{hospital.doctors}</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Established</span>
                    <span className="font-semibold text-gray-900" data-testid="established-year">{hospital.established}</span>
                  </div>
                  
                  {hospital.statistics && (
                    <>
                      <div className="flex items-center justify-between py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-600">Success Rate</span>
                        <span className="font-semibold text-green-600" data-testid="success-rate">{hospital.statistics.success_rate}%</span>
                      </div>
                      
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-600">International Patients</span>
                        <span className="font-semibold text-gray-900" data-testid="intl-patients">{hospital.statistics.international_patients}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="specialties-section">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">Medical Specialties</h2>
          <div className="flex flex-wrap gap-3">
            {hospital.specialties && hospital.specialties.map((specialty, idx) => (
              <Badge
                key={idx}
                variant="outline"
                className="px-4 py-2 text-base border-2 border-orange-200 hover:bg-orange-50 transition-colors"
                data-testid={`specialty-${idx}`}
              >
                {specialty}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Available Treatments */}
      {hospital.treatments && hospital.treatments.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="treatments-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">Available Treatments</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hospital.treatments.map((treatment, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow" data-testid={`treatment-card-${idx}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{treatment}</h3>
                        <p className="text-sm text-gray-600">Advanced medical care with experienced specialists</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Affiliated Doctors */}
      {hospital.affiliated_doctors && hospital.affiliated_doctors.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="doctors-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">Our Medical Experts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hospital.affiliated_doctors.slice(0, 6).map((doctor, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow" data-testid={`doctor-card-${idx}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{doctor.full_name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{doctor.specialization}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>{doctor.experience_years} years experience</span>
                        </div>
                        {doctor.rating > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-sm font-semibold">{doctor.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Patient Testimonials */}
      {hospital.testimonials && hospital.testimonials.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-yellow-50" data-testid="testimonials-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4 text-center">Patient Testimonials</h2>
            <p className="text-center text-gray-600 mb-12">Hear from our international patients</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hospital.testimonials.map((testimonial, idx) => (
                <Card key={idx} className="border-2 border-orange-100" data-testid={`testimonial-${idx}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(testimonial.rating)
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-sm font-semibold text-gray-900">{testimonial.rating}</span>
                    </div>
                    
                    <p className="text-gray-700 mb-4 leading-relaxed">{testimonial.review}</p>
                    
                    <div className="border-t border-gray-200 pt-4">
                      <p className="font-semibold text-gray-900">{testimonial.patient_name}</p>
                      <p className="text-sm text-gray-600">{testimonial.country} • {testimonial.treatment}</p>
                      <p className="text-xs text-gray-500 mt-1">{testimonial.date}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Facilities & Labs */}
      {hospital.facilities && hospital.labs && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="facilities-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-12 text-center">World-Class Facilities</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* General Facilities */}
              <div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <Building className="w-6 h-6 text-orange-600" />
                  Hospital Facilities
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {hospital.facilities.map((facility, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-orange-50 transition-colors"
                      data-testid={`facility-${idx}`}
                    >
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-800">{facility}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lab Facilities */}
              <div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <Activity className="w-6 h-6 text-orange-600" />
                  Diagnostic & Lab Services
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {hospital.labs.map((lab, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-orange-50 transition-colors"
                      data-testid={`lab-${idx}`}
                    >
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-800">{lab}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Statistics */}
      {hospital.statistics && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="statistics-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-12 text-center">Our Track Record</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center border-2 border-orange-200">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 mb-2" data-testid="stat-success-rate">
                    {hospital.statistics.success_rate}%
                  </div>
                  <p className="text-gray-600 font-medium">Success Rate</p>
                </CardContent>
              </Card>

              <Card className="text-center border-2 border-orange-200">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 mb-2" data-testid="stat-intl-patients">
                    {hospital.statistics.international_patients}
                  </div>
                  <p className="text-gray-600 font-medium">International Patients</p>
                </CardContent>
              </Card>

              <Card className="text-center border-2 border-orange-200">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 mb-2" data-testid="stat-surgeries">
                    {hospital.statistics.surgeries_per_year}
                  </div>
                  <p className="text-gray-600 font-medium">Surgeries Annually</p>
                </CardContent>
              </Card>

              <Card className="text-center border-2 border-orange-200">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 mb-2" data-testid="stat-satisfaction">
                    {hospital.statistics.patient_satisfaction}
                  </div>
                  <p className="text-gray-600 font-medium">Patient Satisfaction</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="cta-section">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Ready to Start Your Treatment Journey?
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Connect with our medical experts at {hospital.name} and get personalized treatment plans
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-orange-600 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
              onClick={handleRequestConsultation}
              data-testid="request-consultation-btn"
            >
              Request Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-semibold text-lg px-8 py-6"
              onClick={() => navigate('/hospitals')}
              data-testid="explore-hospitals-btn"
            >
              Explore More Hospitals
            </Button>
          </div>
        </div>
      </section>
      <ConsultationRequestDialog
        open={consultOpen}
        onOpenChange={setConsultOpen}
        context={{ type: 'hospital', name: hospital.name, treatmentCode: 'other' }}
      />
    </div>
  );
}
